import type { Cell, ItemKey, Piece, Pos } from "./types";
import { ITEMS } from "./items";
import { bounding, rotate } from "./geometry";
import { canPlace, writeOcc, clearOcc } from "./board";
import { state } from "./state";
import { piecesEl, trayEl, stageEl } from "../dom";
import { onPointerDown, rotateDragging } from "./drag";
import { refreshTrayState } from "./flow";

/** All live pieces by id (tray + grid). */
export const piecesMap = new Map<number, Piece>();

export function setPieceVisual(p: Piece): void {
  const { h, w } = bounding(p.cells);
  const shape = p.el.querySelector(".pshape") as HTMLElement;
  shape.style.gridTemplateColumns = `repeat(${w},1fr)`;
  shape.style.gridTemplateRows = `repeat(${h},1fr)`;
  const filled = new Set(p.cells.map(([r, c]) => r + "," + c));
  shape.innerHTML = "";
  for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) {
    const d = document.createElement("div");
    d.className = "pcell " + (filled.has(r + "," + c) ? "fill" : "empty");
    shape.appendChild(d);
  }
  sizePiece(p);
}

export function sizePiece(p: Piece): void {
  const { h, w } = bounding(p.cells);
  const cellPx = state.cellPx;
  p.el.style.width = w * cellPx + "px";
  p.el.style.height = h * cellPx + "px";
  const icon = p.el.querySelector(".picon") as HTMLElement;
  const iSize = Math.min(Math.min(w, h) * cellPx * 0.62, cellPx * 1.05);
  icon.style.fontSize = iSize + "px";
  if (p.location === "grid" && p.pos) {
    p.el.style.left = p.pos.c * cellPx + "px";
    p.el.style.top = p.pos.r * cellPx + "px";
  }
}

export function makePiece(key: ItemKey): Piece {
  const item = ITEMS[key];
  const el = document.createElement("div");
  const p: Piece = {
    id: state.nextId++,
    key,
    item,
    cells: item.cells.map((c) => c.slice() as Cell),
    location: "tray",
    pos: null,
    el,
  };
  el.className = "piece rarity-" + item.rarity;
  el.innerHTML = `<div class="pshape"></div><div class="picon">${item.icon}</div><div class="pval">${item.value}</div>`;
  el.title = `${item.name} — value ${item.value}`;
  el.addEventListener("pointerdown", (e) => onPointerDown(e, p));
  el.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    const drag = state.drag;
    if (drag && drag.piece === p && drag.started) rotateDragging();
    else rotatePieceInPlace(p);
  });
  piecesMap.set(p.id, p);
  setPieceVisual(p);
  return p;
}

export function placeInGrid(p: Piece, gr: number, gc: number, doFloat: boolean): void {
  p.location = "grid";
  p.pos = { r: gr, c: gc };
  p.el.classList.remove("dragging");
  p.el.classList.add("in-grid");
  p.el.style.position = "absolute";
  piecesEl.appendChild(p.el);
  sizePiece(p);
  writeOcc(p);
  if (doFloat) floatAt("+" + p.item.value, gr, gc, "gain");
}

export function returnToTray(p: Piece, lossFloat: boolean, prevPos: Pos | null): void {
  if (lossFloat && prevPos) floatAt("−" + p.item.value, prevPos.r, prevPos.c, "loss");
  p.location = "tray";
  p.pos = null;
  p.el.classList.remove("in-grid", "dragging");
  p.el.style.position = "";
  p.el.style.left = "";
  p.el.style.top = "";
  trayEl.appendChild(p.el);
  sizePiece(p);
  refreshTrayState();
}

export function floatAt(text: string, gr: number, gc: number, type: "gain" | "loss"): void {
  const cellPx = state.cellPx;
  const f = document.createElement("div");
  f.className = "float " + type;
  f.textContent = text;
  f.style.left = gc * cellPx + cellPx * 0.2 + "px";
  f.style.top = gr * cellPx + "px";
  stageEl.appendChild(f);
  setTimeout(() => f.remove(), 900);
}

export function rotatePieceInPlace(p: Piece): void {
  if (p.location === "tray") {
    p.cells = rotate(p.cells);
    setPieceVisual(p);
    return;
  }
  // in grid: rotate, then search nearby cells to keep it fitting
  const nc = rotate(p.cells);
  const { r, c } = p.pos!;
  clearOcc(p);
  const cand: [number, number, number][] = [];
  for (let R = r - 2; R <= r + 2; R++) for (let C = c - 2; C <= c + 2; C++) {
    cand.push([R, C, Math.abs(R - r) + Math.abs(C - c)]);
  }
  cand.sort((a, b) => a[2] - b[2]);
  for (const [R, C] of cand) {
    if (canPlace(nc, R, C, p.id)) {
      p.cells = nc;
      p.pos = { r: R, c: C };
      setPieceVisual(p);
      writeOcc(p);
      return;
    }
  }
  writeOcc(p); // revert (old cells)
}

export function hoardValue(): number {
  let s = 0;
  for (const p of piecesMap.values()) if (p.location === "grid") s += p.item.value;
  return s;
}
